/**
 * SF-4713: the demo login. Every demo user in one demo company shares one password, which sf-api
 * keeps (encrypted) for as long as the company exists. The password is hidden until View is
 * pressed; View reads it from sf-api, Hide masks it again, and a later View reads it again. It is
 * never read on page load and is kept only in this component's state (never in the query cache).
 *
 * SF-4770: below the login, every user the build made (from the build read's `demo_users`) and a
 * line saying they all share this password. Hidden when sf-api sends no users.
 */
import { useState } from 'react';
import { useDataProvider } from '@refinedev/core';
import { Alert, Button, Descriptions, Space, Table, Tag, Typography } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import {
  DEMO_LOGIN_NOT_READY,
  demoErrorCode,
  demoErrorMessage,
  demoLoginUrl,
  type DemoBuildRunView,
  type DemoLogin,
  type DemoUser,
} from '../api/demoBuildsClient';

const MASK = '••••••••';
const NOT_READY = 'The demo login is not ready yet.';
export const SAME_PASSWORD = 'Every user below signs in with this password. Start with the suggested login.';

/** sf-api sends them in order (demo login, then Admin, Manager, Staff, then name); kept as sent. */
const DemoUsers: React.FC<{ users: DemoUser[] }> = ({ users }) => (
  <Space direction="vertical" size={4} style={{ width: '100%' }} data-testid="demo-users">
    <Typography.Text>{SAME_PASSWORD}</Typography.Text>
    <Table<DemoUser>
      size="small"
      rowKey="id"
      pagination={false}
      dataSource={users}
      columns={[
        {
          title: 'Name',
          dataIndex: 'name',
          render: (name: string, user) => (
            <Space size={4} wrap>
              {name}
              {user.is_demo_login && <Tag color="blue">Suggested login</Tag>}
            </Space>
          ),
        },
        {
          title: 'Email',
          dataIndex: 'email',
          render: (email: string) => <Typography.Text copyable>{email}</Typography.Text>,
        },
        { title: 'Role', dataIndex: 'role' },
        { title: 'Locations', dataIndex: 'location_count', align: 'right' },
      ]}
    />
  </Space>
);

export const DemoLoginPanel: React.FC<{
  buildId: string;
  login: DemoBuildRunView['demo_login'];
  /** The build's `demo_users`; absent from an older sf-api. */
  users?: DemoUser[];
}> = ({ buildId, login, users }) => {
  const dataProvider = useDataProvider();
  const [shown, setShown] = useState<DemoLogin | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [reading, setReading] = useState(false);

  if (!login) {
    return (
      <Typography.Paragraph type="secondary">
        Eva did not make a demo login for this build.
      </Typography.Paragraph>
    );
  }

  const view = async () => {
    setReading(true);
    setFailure(null);
    try {
      const { data } = await dataProvider().custom!<DemoLogin>({
        url: demoLoginUrl(buildId),
        method: 'get',
      });
      setShown(data);
    } catch (error) {
      setFailure(
        demoErrorCode(error) === DEMO_LOGIN_NOT_READY
          ? NOT_READY
          : demoErrorMessage(error, 'Could not read the demo login. Try again.'),
      );
    } finally {
      setReading(false);
    }
  };

  const email = shown?.email ?? login.email;

  return (
    <Space direction="vertical" style={{ width: '100%' }} data-testid="demo-login">
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="Email">
          <Typography.Text copyable>{email}</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="Password">
          {shown ? (
            <Space>
              <Typography.Text code copyable data-testid="demo-login-password">
                {shown.password}
              </Typography.Text>
              <Button size="small" icon={<EyeInvisibleOutlined />} onClick={() => setShown(null)}>
                Hide
              </Button>
            </Space>
          ) : (
            <Space>
              <Typography.Text data-testid="demo-login-password-masked">{MASK}</Typography.Text>
              {/* Loading ignores further clicks, so a double click reads the password once. */}
              <Button size="small" icon={<EyeOutlined />} loading={reading} onClick={view}>
                View
              </Button>
            </Space>
          )}
        </Descriptions.Item>
      </Descriptions>
      {failure && (
        <Alert type={failure === NOT_READY ? 'info' : 'error'} showIcon message={failure} />
      )}
      {(users?.length ?? 0) > 0 && <DemoUsers users={users!} />}
    </Space>
  );
};
